/*
 * Copyright (c) 2026 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface RoutedOutletResult<T = unknown> {
  changed: boolean;
  data: T | null;
}

interface OutletSession {
  resolve: (result: RoutedOutletResult) => void;
  changed: boolean;
  data: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class RoutedOutletService {
  private router = inject(Router);
  private sessions = new Map<string, OutletSession>();
  private activeOutlet: string | null = null;

  open<T = unknown>(outletPath: any[]): Promise<RoutedOutletResult<T>> {
    const outletName = this.extractOutletName(outletPath);
    if (!outletName) {
      throw new Error('Could not extract outlet name from path');
    }

    // If there's an existing session for this outlet, resolve it before starting a new one
    this.resolve(outletName);

    return new Promise<RoutedOutletResult<T>>((resolve) => {
      this.sessions.set(outletName, {
        resolve: resolve as (result: RoutedOutletResult) => void,
        changed: false,
        data: null,
      });
      this.activeOutlet = outletName;
      this.router.navigate(outletPath);
    });
  }

  /** Called by child components on every successful save/mutation */
  emitData(data?: unknown): void {
    if (!this.activeOutlet) return;
    const session = this.sessions.get(this.activeOutlet);
    if (session) {
      session.changed = true;
      if (data !== undefined) {
        session.data = data;
      }
    }
  }

  /** Called by shell components (RoutePopupFullComponent, etc.) in ngOnDestroy */
  resolve(outletName: string): void {
    const session = this.sessions.get(outletName);
    if (session) {
      session.resolve({
        changed: session.changed,
        data: session.data,
      });
      this.sessions.delete(outletName);
      if (this.activeOutlet === outletName) {
        this.activeOutlet = null;
      }
    }
  }

  private extractOutletName(outletPath: any[]): string | null {
    for (const segment of outletPath) {
      if (segment && typeof segment === 'object' && segment.outlets) {
        return Object.keys(segment.outlets)[0] ?? null;
      }
    }
    return null;
  }
}
